/*

? Forwarding messages on the website (18 Oct 2026, the app's way; lib/chat/forwardMessages.ts):
? - ChatForwardBar: in the composer's place while messages are picked: a close, "N selected", and
?   the forward button.
? - ChatForwardDialog: the chats to send them to, up to FORWARD_MAX_CHATS, a search once there are
?   more than five (the app's rule, counted with no search term), and, once one is picked, a row
?   fixed at the bottom with the count on the left and "Forward" on the right.
? A forward is a new message from the forwarder in each chat; there is no "Forwarded" label.

*/

/* Common Imports */

import { useTheme } from "@mui/system";
import React from "react";

/* Component Imports */

import { Avatar, Checkbox, CircularProgress, Dialog, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close, ShortcutOutlined } from "@mui/icons-material";

/** How many chats one forward may go to (lib/chat/forwardMessages.ts FORWARD_MAX_CHATS). */
export const FORWARD_MAX_CHATS: number = 5;

/** How many messages one forward may carry (FORWARD_MAX_MESSAGES). */
export const FORWARD_MAX_MESSAGES: number = 30;

/** The message kinds that can be forwarded, the server's list. */
const FORWARDABLE_TYPES: Array<string> = ["text", "photo", "document", "voice_note", "attachments", "shared_entity", "location"];

/** Whether [message] can be forwarded: on the server, not deleted, not a card, a contact or an enquiry. */
export function canForwardMessage(message: any): boolean {
	if (!message?._id || message.is_deleted === true || message.is_broadcast === true) return false;
	return FORWARDABLE_TYPES.includes(String(message.message_type || "text"));
}

const lang = (locale?: string) => {
	const ar: boolean = locale === "ar-AE";
	const kn: boolean = locale === "kn-IN";
	return {
		selected: (n: number) => (ar ? `${n} محددة` : kn ? `${n} ಆಯ್ಕೆಯಾಗಿದೆ` : `${n} selected`),
		of_selected: (n: number) => (ar ? `${n} من ${FORWARD_MAX_CHATS} محددة` : kn ? `${FORWARD_MAX_CHATS} ರಲ್ಲಿ ${n} ಆಯ್ಕೆಯಾಗಿದೆ` : `${n} of ${FORWARD_MAX_CHATS} selected`),
		forward_to: ar ? "إعادة التوجيه إلى" : kn ? "ಇದಕ್ಕೆ ಫಾರ್ವರ್ಡ್ ಮಾಡಿ" : "Forward to",
		forward: ar ? "إعادة توجيه" : kn ? "ಫಾರ್ವರ್ಡ್ ಮಾಡಿ" : "Forward",
		search: ar ? "ابحث في المحادثات" : kn ? "ಚಾಟ್‌ಗಳನ್ನು ಹುಡುಕಿ" : "Search chats",
		empty: ar ? "لا توجد محادثات" : kn ? "ಯಾವುದೇ ಚಾಟ್‌ಗಳಿಲ್ಲ" : "No chats to forward to",
		limit: ar
			? `يمكنك إعادة التوجيه إلى ${FORWARD_MAX_CHATS} محادثات كحد أقصى في كل مرة`
			: kn
				? `ಒಮ್ಮೆಗೆ ${FORWARD_MAX_CHATS} ಚಾಟ್‌ಗಳಿಗೆ ಮಾತ್ರ ಫಾರ್ವರ್ಡ್ ಮಾಡಬಹುದು`
				: `You can forward to up to ${FORWARD_MAX_CHATS} chats at a time`,
		stop: ar ? "إيقاف التحديد" : kn ? "ಆಯ್ಕೆ ನಿಲ್ಲಿಸಿ" : "Stop selecting",
	};
};

/** The composer's place while messages are picked. */
export const ChatForwardBar = ({
	count,
	onCancel,
	onForward,
	locale,
}: {
	count: number;
	onCancel: () => void;
	onForward: () => void;
	locale?: string;
}) => {
	const words = lang(locale);
	return (
		<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", padding: "0.5rem 0.75rem" }}>
			<IconButton aria-label={words.stop} onClick={onCancel} size="small">
				<Close fontSize="small" />
			</IconButton>
			<Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.9375rem" }}>{words.selected(count)}</Typography>
			<BeegruButton flavor="primary" variant="contained" onClick={onForward} sx={{ borderRadius: "1rem", minWidth: 0 }} aria-label={words.forward}>
				<ShortcutOutlined sx={{ fontSize: "1.375rem" }} />
			</BeegruButton>
		</div>
	);
};

/** One row of the chat list as the picker needs it. */
type PickerChat = { id: string; name: string; picture: string };

/** The other side's name and picture of a fetch-user-chats row, the list card's rule. */
function pickerChatOf(chat: any, profileContext: any): PickerChat | null {
	if (!chat?._id || chat.chat_source_collection === "broadcast" || chat.is_request === true || chat.is_unavailable === true) return null;
	const isActing = (p: any): boolean =>
		profileContext?.is_business_profile === true
			? p?.is_business_profile === true && String(p.business_profile_id?._id ?? p.business_profile_id) === String(profileContext.business_profile_id)
			: p?.is_business_profile !== true && String(p.user_id?._id ?? p.user_id) === String(profileContext?.user_id);
	const other: any = (chat.participants || []).find((p: any) => !isActing(p)) || (chat.participants || [])[0];
	const business: any = other?.is_business_profile === true && typeof other.business_profile_id === "object" ? other.business_profile_id : null;
	const user: any = other?.is_business_profile !== true && typeof other?.user_id === "object" ? other.user_id : null;
	const name: string = business
		? String(business.title || "")
		: user
			? `${user.first_name || ""} ${user.last_name || ""}`.trim()
			: String(chat.chat_title || "");
	const file: string = business ? String(business.display_picture_url?.file_id || "") : user ? String(user.profile_picture?.file_id || "") : "";
	const picture: string = !file || file.includes("/images/icons/Avatar.svg") ? "/images/icons/Avatar.svg" : `/profile-picture/${file}`;
	return { id: String(chat._id), name: name || "Chat", picture };
}

/** Where the picked messages go. [onForward] sends them and resolves when it's done. */
export const ChatForwardDialog = ({
	open,
	onClose,
	onForward,
	profile_context,
	session,
	locale,
}: {
	open: boolean;
	onClose: () => void;
	onForward: (targetChatIds: Array<string>) => Promise<void>;
	profile_context: any;
	session: any;
	locale?: string;
}) => {
	const theme = useTheme();
	const words = lang(locale);
	const [query, setQuery] = React.useState<string>("");
	const [chats, setChats] = React.useState<Array<PickerChat>>([]);
	const [total, setTotal] = React.useState<number>(0);
	const [page, setPage] = React.useState<number>(1);
	const [hasMore, setHasMore] = React.useState<boolean>(false);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [picked, setPicked] = React.useState<Array<string>>([]);
	const [sending, setSending] = React.useState<boolean>(false);
	const [limitHit, setLimitHit] = React.useState<boolean>(false);

	const load = React.useCallback(
		async (nextPage: number, term: string) => {
			setLoading(true);
			try {
				const response = await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-user-chats?search_term=" + encodeURIComponent(term), {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify({
						is_business_profile: profile_context?.is_business_profile === true,
						user_id: session?.user?.dbUser?._id || profile_context?.user_id,
						business_profile_id: profile_context?.business_profile_id,
						page: nextPage,
						search_term: term,
						sessionId: session?.session_id || "",
					}),
				});
				const result = await response.json().catch(() => null);
				const rows: Array<any> = result?.payload?.chats || [];
				const mapped = rows.map((row) => pickerChatOf(row, profile_context)).filter(Boolean) as Array<PickerChat>;
				setChats((current) => (nextPage === 1 ? mapped : [...current, ...mapped.filter((c) => !current.some((x) => x.id === c.id))]));
				setPage(nextPage);
				setHasMore(rows.length >= 10);
				// Only an unfiltered load sets the total the search's visibility reads.
				if (!term) setTotal(Number(result?.payload?.chats_count || rows.length));
			} catch {
				// The list keeps what it has.
			} finally {
				setLoading(false);
			}
		},
		[profile_context, session],
	);

	// A fresh picker on every opening.
	React.useEffect(() => {
		if (!open) return;
		setQuery("");
		setPicked([]);
		setSending(false);
		setLimitHit(false);
		load(1, "");
	}, [open, load]);

	// The search, a moment after typing stops.
	React.useEffect(() => {
		if (!open) return;
		const timer = setTimeout(() => load(1, query.trim()), 300);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	const toggle = (id: string) => {
		if (sending) return;
		setPicked((current) => {
			if (current.includes(id)) return current.filter((x) => x !== id);
			if (current.length >= FORWARD_MAX_CHATS) {
				setLimitHit(true);
				return current;
			}
			setLimitHit(false);
			return [...current, id];
		});
	};

	const submit = async () => {
		if (sending || picked.length === 0) return;
		setSending(true);
		await onForward(picked);
		setSending(false);
	};

	return (
		<Dialog
			open={open}
			onClose={() => (sending ? null : onClose())}
			fullWidth
			maxWidth="xs"
			dir={locale === "ar-AE" ? "rtl" : "ltr"}
			PaperProps={{ sx: { height: "75vh", display: "flex", flexDirection: "column" } }}
		>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600 }}>
				{words.forward_to}
				<IconButton size="small" onClick={onClose} disabled={sending} aria-label="Close">
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>
			{total > 5 || query ? (
				<div style={{ padding: "0 1.5rem 0.5rem 1.5rem" }}>
					<TextField fullWidth size="small" placeholder={words.search} value={query} onChange={(e) => setQuery(e.target.value)} />
				</div>
			) : null}
			{limitHit ? (
				<Typography variant="body2" sx={{ color: "warning.dark", padding: "0 1.5rem 0.5rem 1.5rem" }}>
					{words.limit}
				</Typography>
			) : null}
			<div
				style={{ flex: 1, overflowY: "auto", padding: "0 0.75rem" }}
				onScroll={(event) => {
					const el = event.currentTarget;
					if (hasMore && !loading && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) load(page + 1, query.trim());
				}}
			>
				{chats.length === 0 && !loading ? (
					<Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", padding: "2rem 0" }}>
						{words.empty}
					</Typography>
				) : null}
				{chats.map((chat) => (
					<div
						key={chat.id}
						onClick={() => toggle(chat.id)}
						style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0.75rem", cursor: "pointer", borderRadius: "0.75rem" }}
					>
						<Avatar src={chat.picture} alt={chat.name} variant="rounded" sx={{ width: 40, height: 40 }} />
						<Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.9375rem" }} noWrap>
							{chat.name}
						</Typography>
						<Checkbox checked={picked.includes(chat.id)} disabled={sending} size="small" tabIndex={-1} />
					</div>
				))}
				{loading ? (
					<div style={{ display: "flex", justifyContent: "center", padding: "1rem 0" }}>
						<CircularProgress size={20} />
					</div>
				) : null}
			</div>
			{picked.length > 0 ? (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "0.75rem 1.5rem",
						borderTop: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{words.of_selected(picked.length)}</Typography>
					<BeegruButton variant="text" flavor="primary" onClick={submit} loading={sending} sx={{ textTransform: "none", fontWeight: 600 }}>
						{words.forward}
					</BeegruButton>
				</div>
			) : null}
		</Dialog>
	);
};
